import type {
  IFeature,
  IFeatureData,
  IFeaturePayload,
  IFeatureState,
  IFeaturesAssociations,
  IMarketInfo,
  IOption,
  IPaginatedFeatureData,
  IProductModelResponseData,
  PaginatedState,
  PaginationAction,
} from '../../../types';
import { TableField } from '../../data/Constants';
import {
  fetchValidMarkets,
  flipCountries,
  isLessFeature,
} from '../../services/DataHelperFunctions';

/**
 * To Store the data in state in a required format for page purpose
 * @param {PaginatedState<IProductModelResponseData>} state Current paginated state
 * @param {PaginationAction<IProductModelResponseData>} action Pagination action
 * @returns Updated paginated state
 */
export function getPaginatedData(
  state: PaginatedState<IProductModelResponseData>,
  action: PaginationAction<IProductModelResponseData>
) {
  const newData = { ...state };
  if (action.page === 1) {
    newData['data'] = {}; // To reset data on every load of first page as change in recordLength might affect the functionality
  }
  if (state.data && Object.keys(state.data).length > 3) {
    // Delete the second entry of data element as first element is the initial load data
    if (newData?.data) {
      const keys = Object.keys(newData.data);
      const secondKey = keys[1] === undefined ? undefined : Number(keys[1]);
      if (secondKey !== undefined) {
        delete newData.data[secondKey];
      }
    }
  }
  newData['searchKey'] = action?.searchKey ?? '';
  action.data = action.data.map((item, index) => ({
    ...item,
    id: `${action.page}-${index + 1}`,
  }));
  newData['data'] = {
    ...newData['data'],
    [action.page]: action.data ?? [],
  };
  newData['page'] = action.page;
  newData['totalCount'] = action.totalRecords;
  newData['limit'] = action.limit ?? 15;
  return { ...newData };
}

/**
 * Update table data with new page/filter information
 * @param {IFeatureState} state Current feature state
 * @param {IFeaturePayload} action Feature payload action
 * @param {Object} requestPayload Request payload for API
 * @returns Updated feature state
 */
export function updateTableData(
  state: IFeatureState,
  action: IFeaturePayload,
  requestPayload = {}
) {
  // Create a new state object
  const newData = {
    ...state,
    data: { ...state.data },
    requestPayload: { ...requestPayload },
    currentPage: action.currentPage,
    totalCount: action.totalRecords,
    recordsPerPage: action.recordsPerPage ?? 15,
    searchKey: action.searchKey ?? '',
  };

  if (action.currentPage === 1) {
    newData.data = {}; // To reset data on every load of first page
  }

  // Limiting the number of cached pages (adjust '5' as needed)
  const dataKeys = Object.keys(newData.data ?? {});
  if (
    dataKeys.length > 5 &&
    action.currentPage !== 1 &&
    !dataKeys.includes(String(action.currentPage))
  ) {
    // Delete the oldest cached page (assuming keys are page numbers)
    delete newData.data[Math.min(...dataKeys.map(Number))];
  }

  newData.data = {
    ...newData.data,
    [action.currentPage]: action.data ?? [],
  };

  return newData;
}

/**
 * To Create Features in a paginated format
 * After Save is complete and GET call of feature is made, we lose our previous state data
 * for SaveStatus for each feature row. Therefore added additional updatedFeatureData parameter
 * to get hold of SaveStatus.
 *
 * @param {IPaginatedFeatureData} featureData Feature data available from API calls
 * @param {IFeatureData} updatedFeatureData Updated feature data with saveStatus
 * @returns {IFeature[]} Feature rows array
 */
export function createPaginatedFeatures(
  featureData: IPaginatedFeatureData,
  updatedFeatureData: IFeatureData
): IFeature[] {
  const featureRows: IFeature[] = [];
  // Build a map for quick lookup of updated features by code
  const updatedFeatureMap = new Map<string, IFeature>();
  updatedFeatureData?.data?.forEach((f) => {
    updatedFeatureMap.set(f.code, f);
  });

  featureData.data?.forEach((feature: IFeature) => {
    const updatedStateFeatureRow = updatedFeatureMap.get(feature.code);
    if (feature.features && feature.features.length > 0) {
      feature.features.forEach((option: IOption) => {
        if (!isLessFeature(option.code)) {
          featureRows.push({
            ...option,
            id: feature.code + '.' + option.code,
            [TableField.feature]: feature.code + '.' + feature.description,
            [TableField.option]: option.code + '.' + option.description,
            [TableField.type]: feature.familyType,
            [TableField.saveStatus]: updatedStateFeatureRow?.saveStatus,
            familyType: feature.familyType,
          });
        }
      });
    } else {
      featureRows.push({
        ...feature,
        id: feature.code,
        [TableField.feature]: feature.code + '.' + feature.description,
        [TableField.saveStatus]: updatedStateFeatureRow?.saveStatus,
        [TableField.type]: feature.familyType,
      });
    }
  });

  return featureRows;
}

/**
 * Get Feature Status from the API response
 * @param {IPaginatedFeatureData} featureData Feature data from API
 * @returns {Record<string, boolean>} Feature status map with codes as keys
 */
export function getFeatureStatus(featureData: IPaginatedFeatureData): Record<string, boolean> {
  const featureStatus: Record<string, boolean> = {};
  featureData?.data?.forEach((feature) => {
    featureStatus[feature.code] = feature.isActive;
    if (feature.features) {
      feature.features.forEach((option) => {
        featureStatus[feature.code + '.' + option.code] = option.isActive;
      });
    }
  });
  return featureStatus;
}

/**
 * Map the Market associations to Features object
 * @param {IMarketInfo[]} marketAssociations All Market associations
 * @param {string} productCode ProductCode
 * @param {string [] } productMarkets product markets array
 * @returns {IFeaturesAssociations} Feature Market associations
 */
export const mapMAToFeatureState = (
  marketAssociations: IMarketInfo[],
  productCode: string,
  productMarkets: string[],
  allMarkets: string[]
): IFeaturesAssociations => {
  const featureMarketAssociations: IFeaturesAssociations = {};
  //Filter feature Associations, then loop through it to set the Feature Associations Object
  marketAssociations
    .filter((details) => details.code !== productCode)
    .forEach((marketDetails) => {
      marketDetails.countries =
        marketDetails.includeMarkets === false
          ? flipCountries(allMarkets, marketDetails.countries)
          : marketDetails.countries;
      featureMarketAssociations[marketDetails.code] = {
        initialMarkets: marketDetails.countries,
        updatedMarkets: fetchValidMarkets(marketDetails.countries, productMarkets),
      };
    });
  return featureMarketAssociations;
};

/**
 * To remove a Market from all the Features Market association
 * @param {IFeaturesAssociations} featuresMa Features market associations
 * @param {string} market Market code to remove
 * @param {number} productMaLength Product market associations length
 * @returns {IFeaturesAssociations} Updated features market associations
 */
export function removeMarketFeatureMA(
  featuresMa: IFeaturesAssociations,
  market: string,
  productMaLength: number
): IFeaturesAssociations {
  Object.keys(featuresMa).forEach((key) => {
    const index = featuresMa[key].updatedMarkets.indexOf(market);
    if (index >= 0) {
      //Market is present

      //Initial countries are not present which means no rule and this is the last element we are removing from array hence delete
      if (
        featuresMa[key].initialMarkets.length === 0 &&
        featuresMa[key].updatedMarkets.length === 1
      ) {
        delete featuresMa[key];
        return;
      }
      featuresMa[key].updatedMarkets.splice(index, 1);
    } else {
      //Market is not present
      //Initial countries are not present which means no rule and option and product selections are equal so can be deleted
      if (
        featuresMa[key].initialMarkets.length === 0 &&
        featuresMa[key].updatedMarkets.length === productMaLength
      ) {
        delete featuresMa[key];
        return;
      } //Initial countries length is greater than 0 and now following Product model selections so can be made to empty array indicating rule can be deleted
      if (
        featuresMa[key].initialMarkets.length > 0 &&
        featuresMa[key].updatedMarkets.length === productMaLength
      ) {
        featuresMa[key].updatedMarkets = [];
      }
    }
  });
  return featuresMa;
}
